// ---------------------------------------------------------------------------
// API base URL
// ---------------------------------------------------------------------------
// Strategy:
//   1. If REACT_APP_BACKEND_URL is set, use it (local dev)
//   2. In production, use direct cross-origin to admin.oimyai.com
//      The admin backend has proper CORS with credentials support.
//   3. The Vercel rewrite proxy is a nice-to-have but not required.
// Use relative URLs in production so requests go through the Vercel rewrite proxy
// (vercel.json rewrites /api/u/* → admin.oimyai.com/api/u/*)
// This avoids CORS issues entirely since it's same-origin.
export const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ---------------------------------------------------------------------------
// Session token management (localStorage backup for cookie auth)
// ---------------------------------------------------------------------------
// Primary auth is via httpOnly cookie set by the backend.
// We also store the session token in localStorage as a backup
// and send it as Authorization: Bearer in case cookies don't work.

export function getAuthHeaders() {
  const token = getSessionToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function getSessionToken() {
  return localStorage.getItem('oimy_session_token');
}

function setSessionToken(token) {
  if (token) {
    localStorage.setItem('oimy_session_token', token);
  }
}

function clearSessionToken() {
  localStorage.removeItem('oimy_session_token');
}

export function hasSessionToken() {
  return !!getSessionToken();
}

async function request(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };

  // Add session token as Bearer header (backup for cookie auth)
  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Use credentials:'include' so the oimy_session cookie is sent.
  // With the Vercel rewrite proxy, this is same-origin so no CORS issues.
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    let msg;
    try {
      const json = JSON.parse(text);
      msg = json.error || json.message || text;
    } catch {
      msg = text || res.statusText;
    }
    throw new Error(msg);
  }
  const ct = res.headers.get('content-type');
  if (ct && ct.includes('application/json')) {
    const data = await res.json();
    // Auto-store session token from auth responses
    if (data.sessionToken) {
      setSessionToken(data.sessionToken);
    }
    return data;
  }
  return res;
}

// For multipart/form-data uploads (no Content-Type header - browser sets it with boundary)
async function requestMultipart(path, formData) {
  const headers = {};
  
  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });
  
  if (!res.ok) {
    const text = await res.text();
    let msg;
    try {
      const json = JSON.parse(text);
      msg = json.error || json.message || text;
    } catch {
      msg = text || res.statusText;
    }
    throw new Error(msg);
  }
  
  const ct = res.headers.get('content-type');
  if (ct && ct.includes('application/json')) {
    const data = await res.json();
    if (data.sessionToken) {
      setSessionToken(data.sessionToken);
    }
    return data;
  }
  return res;
}

export async function registerEmail(data) {
  return request('/api/u/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function loginEmail(email, password) {
  return request('/api/u/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function completeProfile(data) {
  return request('/api/u/auth/complete-profile', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMe() {
  return request('/api/u/auth/me');
}

export async function logout() {
  clearSessionToken();
  return request('/api/u/auth/logout', { method: 'POST' }).catch(() => {});
}

export async function sendCasualMessage(message, messageType = 'text') {
  return request('/api/u/chat/casual', {
    method: 'POST',
    body: JSON.stringify({ message, message_type: messageType }),
  });
}

export async function sendAgenticMessage(message) {
  return request('/api/u/chat/agentic', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

/**
 * Send agentic message with file attachments
 * Files are sent as multipart/form-data, then converted to base64 for the AI
 */
export async function sendAgenticMessageWithAttachments(message, attachments) {
  const formData = new FormData();
  formData.append('message', message);
  
  // Add each file
  attachments.forEach((att, index) => {
    formData.append(`file_${index}`, att.file);
    formData.append(`file_${index}_type`, att.type);
    formData.append(`file_${index}_name`, att.name);
  });
  
  formData.append('file_count', attachments.length.toString());
  
  return requestMultipart('/api/u/chat/agentic-upload', formData);
}

export async function generateTTS(text, voiceId = 'o7wLjIyo9bvhdUh12QMc') {
  return request('/api/u/tts/generate', {
    method: 'POST',
    body: JSON.stringify({ text, voice_id: voiceId }),
  });
}

export async function saveAgentConfig(config) {
  return Promise.resolve({ success: true });
}

export async function getAgentConfig() {
  return request('/api/u/settings/agent');
}

export async function getMessageHistory(mode = 'casual', limit = 50) {
  return request(`/api/u/chat/history?mode=${mode}&limit=${limit}`);
}

export async function getSTTToken() {
  return request('/api/u/stt/token', { method: 'POST' });
}

export async function checkEmail(email) {
  return request('/api/u/auth/check-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function register(email, password, name, familyCode) {
  return request('/api/u/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name, familyCode }),
  });
}

export async function restartAgent() {
  return request('/api/u/settings/agent/restart', { method: 'POST' });
}

export async function clerkSync(clerkId, email, name, picture) {
  return request('/api/u/auth/clerk-sync', {
    method: 'POST',
    body: JSON.stringify({ clerkId, email, name, picture }),
  });
}

// File upload to Vercel Blob
export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  
  const res = await fetch(`${API_BASE}/api/u/files/upload`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || 'Upload failed');
  }
  
  return res.json();
}

// Get shareable file URL
export function getFileUrl(blobUrl) {
  return blobUrl; // Vercel Blob URLs are already public
}

// Get agents accessible to the current user
export async function getAgents() {
  const res = await fetch(`${API_BASE}/api/u/agents`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to get agents');
  return res.json();
}

// Update user settings (language, preferences)
export async function updateUserSettings(settings) {
  const res = await fetch(`${API_BASE}/api/u/settings`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error('Failed to update settings');
  return res.json();
}

// Get user settings
export async function getUserSettings() {
  const res = await fetch(`${API_BASE}/api/u/settings`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to get settings');
  return res.json();
}

// Get available voices for user
export async function getVoices() {
  const res = await fetch(`${API_BASE}/api/u/voices`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to get voices');
  return res.json();
}

// Push notification subscription
export async function subscribePush(subscription) {
  return request('/api/u/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}
